import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { StylishNewToggleButtonGroup } from 'components/DesignSystems/New/StylishNewToggleButtonGroup';
import FormCheckboxGroupField from 'components/IAP/formFields/FormCheckboxGroupField';
import FormDateTimeField from 'components/IAP/formFields/FormDateTimeField';
import FormSelectField from 'components/IAP/formFields/FormSelectField';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import type { StrikeTeam } from 'components/IAP/hooks/strikeTeamHooks';
import WorkspaceFilePicker, {
  type WorkspaceFileRef,
} from 'components/IAP/actionTicket/WorkspaceFilePicker';
import apiConfig from 'constants/apiConfig';
import { useHwcgFlag } from 'utils/featureFlags';
import { useAors } from 'hooks/useAors';
import {
  CreateNotificationFormFields,
  NotificationPriority,
  NotificationType,
  useAudiencePreview,
} from 'hooks/useNotification';
import { useRosters } from 'hooks/useRosters';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import { registerFocNotificationsTourApi } from '../../focNotificationsTour/focNotificationsTourBridge';
import ChannelMessageInput from './ChannelMessageInput';
import { severityLabel } from './notificationSeverityUi';
import AudienceAreaField from './AudienceAreaField';
import EscalationPlanField from './EscalationPlanField';
import {
  ESCALATION_PRESETS,
  EscalationPresetKey,
  escalationPlanErrors,
  escalationPlanForSubmit,
} from './escalationPlanModel';
import {
  describeSmsResponseOptions,
  RESPONSE_PRESET_CHOICES,
  RESPONSE_PRESETS,
  ResponsePresetKey,
} from './responseOptions';
import {
  AreaAudience,
  AudienceMode,
  EMPTY_AREA_AUDIENCE,
  RoleScopeMember,
  audienceError,
  audienceForSubmit,
  filterRecipientIds,
  hasAreaSelection,
  parseRoleScopeKeys,
  roleScopeOptions,
  unionRecipientIds,
  userIdsForSelectedRoles,
} from './audienceModel';

type NotificationComposeForm = CreateNotificationFormFields & {
  roleScopeKeys: string[];
};

const notificationTypeOptions = [
  {
    label: 'PRATUS',
    value: NotificationType.IN_APP,
  },
  {
    label: NotificationType.EMAIL,
    value: NotificationType.EMAIL,
  },
  {
    label: NotificationType.SMS.toUpperCase(),
    value: NotificationType.SMS,
  },
  {
    label: 'Microsoft Teams',
    value: NotificationType.TEAMS,
    disabled: true,
    tooltip: 'Upgrade to Enable',
  },
  {
    label: 'Voice (TTS cell)',
    value: NotificationType.TTS_CELL,
    disabled: true,
    tooltip: 'Upgrade to Enable',
  },
];

enum DeliveryMode {
  NOW = 'now',
  SCHEDULE = 'schedule',
}

const EMPTY_AUDIENCE_MESSAGE = 'Please select at least one individual';

export type NotificationComposeCollect =
  | { status: 'empty' }
  | { status: 'error'; message: string }
  | { status: 'ready'; fields: CreateNotificationFormFields };

export type NotificationComposeHandle = {
  /**
   * `allowEmpty` treats “nobody selected” as an omitted send. A role or area
   * that matches nobody is still an error.
   */
  collect: (options?: { allowEmpty?: boolean }) => NotificationComposeCollect;
};

export type NotificationComposeSeed = Partial<NotificationComposeForm>;

type NotificationComposeFieldsProps = {
  /** Locks Associated Incident when the composer is opened from an incident. */
  incidentId?: string;
  rolesTitle?: string;
  onActivity?: (activity: { summary: string; ready: boolean }) => void;
  onDeliveryMode?: (mode: 'now' | 'schedule') => void;
  /**
   * Event and rule notifications send when the event is created. Standalone
   * Create Notification keeps the schedule control.
   */
  allowSchedule?: boolean;
  /**
   * Values applied on mount. Remount the composer (change `key`) when the
   * source template changes; `defaultValues` are not reapplied afterward.
   */
  seed?: NotificationComposeSeed;
};

const NotificationComposeFields = forwardRef<
  NotificationComposeHandle,
  NotificationComposeFieldsProps
>(function NotificationComposeFields(
  {
    incidentId,
    rolesTitle = 'Roles',
    onActivity,
    onDeliveryMode,
    allowSchedule = true,
    seed,
  },
  ref
) {
  const [deliveryMode, setDeliveryMode] = useState<
    DeliveryMode.SCHEDULE | DeliveryMode.NOW
  >(DeliveryMode.NOW);
  const [attachments, setAttachments] = useState<WorkspaceFileRef[]>([]);
  const hwcg = useHwcgFlag();
  const form = useForm<NotificationComposeForm>({
    mode: 'onChange',
    defaultValues: {
      acknowledgementRequired: false,
      autoArchiveDate: null,
      priority: NotificationPriority.INFO,
      emailSubject: '',
      emailMessage: '',
      smsMessage: '',
      teamsMessage: '',
      phoneMessage: '',
      appMessage: '',
      userIds: [],
      teamIds: [],
      sourceItemId: null,
      sourceItemType: null,
      scheduledDate: null,
      notificationTypes: [NotificationType.IN_APP],
      aors: [],
      ...seed,
      incidentId: incidentId ?? seed?.incidentId ?? '',
      roleScopeKeys: seed?.roleScopeKeys ?? [],
    },
  });
  const { control } = form;
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: rosters } = useRosters([groupGuid]);
  const strikeTeamIncidentId = useActualIncidentId();
  const { data: strikeTeams = [] } = useQuery({
    queryKey: ['notification-strike-teams', strikeTeamIncidentId],
    enabled: hwcg && Boolean(strikeTeamIncidentId),
    queryFn: async () => {
      const response = await axios.get<StrikeTeam[]>(
        `${apiConfig.apiGateway.ics_strike_teams}/${strikeTeamIncidentId}`
      );
      return Array.isArray(response.data) ? response.data : [];
    },
  });
  const { data: roleScopeMembers = [] } = useQuery({
    queryKey: ['role-scope-members', groupGuid],
    enabled: Boolean(groupGuid),
    queryFn: async () => {
      const response = await axios.post<RoleScopeMember[]>(
        apiConfig.apiGateway.roleScopeMembersForGroup,
        { group_guid: groupGuid }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
  });
  const { data: incidents } = useAllIncidents();
  const { data: aors } = useAors();

  const selectedPriority = useWatch({ control, name: 'priority' });
  const acknowledgementRequired = useWatch({
    control,
    name: 'acknowledgementRequired',
  });
  const selectedChannels = useWatch({ control, name: 'notificationTypes' });
  const selectedUserIds = useWatch({ control, name: 'userIds' }) ?? [];
  const selectedRoleScopeKeys =
    useWatch({ control, name: 'roleScopeKeys' }) ?? [];
  const selectedTeamIds = useWatch({ control, name: 'teamIds' }) ?? [];
  const watchedIncidentId = useWatch({ control, name: 'incidentId' });
  const associatedIncidentId = watchedIncidentId || incidentId || null;
  const [responsePreset, setResponsePreset] =
    useState<ResponsePresetKey>('receipt');
  const [escalationPreset, setEscalationPreset] =
    useState<EscalationPresetKey>('none');
  const [areaAudience, setAreaAudience] =
    useState<AreaAudience>(EMPTY_AREA_AUDIENCE);
  const [sendToEveryone, setSendToEveryone] = useState(false);

  const escalationPlan = ESCALATION_PRESETS[escalationPreset];

  const areaFilterActive = hasAreaSelection(areaAudience);
  const audiencePreview = useAudiencePreview({
    geometry: areaAudience.geometry,
    aorItemIds: areaAudience.aorItemIds,
    notificationTypes: selectedChannels,
    enabled: areaFilterActive,
  });

  const aorOptions =
    aors
      ?.filter((aor) => Boolean(aor.item_id))
      .map((aor) => ({ label: aor.name, value: aor.item_id as string })) ?? [];

  const matchUserIds = audiencePreview.data?.userGuids ?? [];
  const resolvedRoleUserIds = userIdsForSelectedRoles(
    roleScopeMembers,
    parseRoleScopeKeys(selectedRoleScopeKeys)
  );
  const peopleTeamIds = hwcg ? selectedTeamIds : [];
  const combinedUserIds = unionRecipientIds(
    selectedUserIds,
    resolvedRoleUserIds
  );
  const peopleUserIds = areaFilterActive
    ? filterRecipientIds(combinedUserIds, matchUserIds)
    : combinedUserIds;
  const everyoneInArea = sendToEveryone && areaFilterActive;
  const peopleProblem =
    !audiencePreview.isFetching &&
    (selectedRoleScopeKeys.length > 0 || areaFilterActive)
      ? audienceError({
          mode: AudienceMode.PEOPLE,
          audience: areaAudience,
          userIds: peopleUserIds,
          previewCount: areaFilterActive
            ? (audiencePreview.data?.userCount ?? null)
            : null,
          roleScopes: selectedRoleScopeKeys,
          teamIds: peopleTeamIds,
          sendToEveryone: everyoneInArea,
          filterActive: areaFilterActive,
        })
      : null;

  const handleChannelChange = (nextChannels: string | string[] | undefined) => {
    const channels = nextChannels as NotificationType[];
    if (!channels.includes(NotificationType.EMAIL)) {
      form.setValue('emailSubject', '');
      form.setValue('emailMessage', '');
    }
    if (!channels.includes(NotificationType.IN_APP)) {
      form.setValue('appMessage', '');
    }
    if (!channels.includes(NotificationType.TEAMS)) {
      form.setValue('teamsMessage', '');
    }
    if (!channels.includes(NotificationType.SMS)) {
      form.setValue('smsMessage', '');
    }
  };

  useEffect(() => {
    if (!areaFilterActive && sendToEveryone) setSendToEveryone(false);
  }, [areaFilterActive, sendToEveryone]);

  useEffect(() => {
    if (!areaFilterActive || !audiencePreview.data || everyoneInArea) return;
    const allowed = new Set(audiencePreview.data.userGuids ?? []);
    const current = form.getValues('userIds') ?? [];
    const next = current.filter((id) => allowed.has(id));
    if (next.length !== current.length) form.setValue('userIds', next);
  }, [areaFilterActive, audiencePreview.data, everyoneInArea, form]);

  useEffect(() => {
    onDeliveryMode?.(
      deliveryMode === DeliveryMode.SCHEDULE ? 'schedule' : 'now'
    );
  }, [deliveryMode, onDeliveryMode]);

  useEffect(() => {
    return registerFocNotificationsTourApi({
      selectStandDownSafety: () => {
        form.setValue('priority', NotificationPriority.STAND_DOWN_SAFETY);
        setDeliveryMode(DeliveryMode.NOW);
        form.setValue('scheduledDate', null);
      },
    });
  }, [form]);

  const audienceCount = everyoneInArea
    ? (audiencePreview.data?.userCount ?? 0)
    : peopleUserIds.length + peopleTeamIds.length;

  useEffect(() => {
    const channelCount = selectedChannels?.length ?? 0;
    const ready = channelCount > 0 && audienceCount > 0 && !peopleProblem;
    const summary = ready
      ? `${channelCount} channel${channelCount === 1 ? '' : 's'} · ${audienceCount} recipient${
          audienceCount === 1 ? '' : 's'
        }`
      : 'Not configured';
    onActivity?.({ summary, ready });
  }, [audienceCount, onActivity, peopleProblem, selectedChannels]);

  useImperativeHandle(
    ref,
    () => ({
      collect: (options) => {
        const formData = form.getValues();
        const namedUserIds = formData.userIds ?? [];
        const roleScopeKeys = formData.roleScopeKeys ?? [];
        const roleUserIds = userIdsForSelectedRoles(
          roleScopeMembers,
          parseRoleScopeKeys(roleScopeKeys)
        );
        const teamIds = hwcg ? (formData.teamIds ?? []) : [];
        const combined = unionRecipientIds(namedUserIds, roleUserIds);
        const audienceUserIds =
          areaFilterActive && !everyoneInArea
            ? filterRecipientIds(
                combined,
                audiencePreview.data?.userGuids ?? []
              )
            : combined;

        if (areaFilterActive && audiencePreview.isFetching) {
          return {
            status: 'error',
            message: 'Still checking who is in this area.',
          };
        }

        const problem = audienceError({
          mode: AudienceMode.PEOPLE,
          audience: areaAudience,
          userIds: audienceUserIds,
          previewCount: areaFilterActive
            ? (audiencePreview.data?.userCount ?? null)
            : null,
          roleScopes: roleScopeKeys,
          teamIds,
          sendToEveryone: everyoneInArea,
          filterActive: areaFilterActive,
        });

        const nothingChosen =
          namedUserIds.length === 0 &&
          roleScopeKeys.length === 0 &&
          teamIds.length === 0 &&
          !areaFilterActive;
        if (options?.allowEmpty && (nothingChosen || !problem)) {
          if (nothingChosen || problem === EMPTY_AUDIENCE_MESSAGE) {
            return { status: 'empty' };
          }
        }
        if (problem) {
          const emptyRole =
            roleScopeKeys.length > 0 &&
            roleUserIds.length === 0 &&
            namedUserIds.length === 0;
          form.setError(emptyRole ? 'roleScopeKeys' : 'userIds', {
            message: problem,
          });
          return { status: 'error', message: problem };
        }
        if (!formData.priority) {
          form.setError('priority', { message: 'Please select a severity' });
          return { status: 'error', message: 'Please select a severity' };
        }
        if (
          formData.acknowledgementRequired &&
          escalationPlan &&
          escalationPlanErrors(escalationPlan).length > 0
        ) {
          return {
            status: 'error',
            message: 'Fix the escalation plan before sending.',
          };
        }

        const { roleScopeKeys: _roleScopeKeys, ...notification } = formData;
        const fields: CreateNotificationFormFields = {
          ...notification,
          notificationTypes: (notification.notificationTypes ?? []).filter(
            (channel) =>
              channel !== NotificationType.TEAMS &&
              channel !== NotificationType.TTS_CELL
          ),
          ...audienceForSubmit({
            audience: areaAudience,
            userIds: audienceUserIds,
            teamIds,
            sendToEveryone: everyoneInArea,
          }),
          responseOptions: formData.acknowledgementRequired
            ? RESPONSE_PRESETS[responsePreset]
            : null,
          escalationPlan: escalationPlanForSubmit(
            escalationPlan,
            formData.acknowledgementRequired
          ),
          fileIds: attachments.map((file) => file.fileId),
        };
        return { status: 'ready', fields };
      },
    }),
    [
      areaAudience,
      areaFilterActive,
      attachments,
      audiencePreview.data,
      audiencePreview.isFetching,
      escalationPlan,
      everyoneInArea,
      form,
      hwcg,
      responsePreset,
      roleScopeMembers,
    ]
  );

  return (
    <FormProvider {...form}>
      <div data-tour="notifications-channels">
        <FormCheckboxGroupField
          title="Communication Channels"
          fieldName="notificationTypes"
          options={notificationTypeOptions}
          onChangeCallback={handleChannelChange}
        />
      </div>
      <div className="mb-3">
        <ChannelMessageInput />
      </div>
      <div className="mb-3">
        <Controller
          control={control}
          name="acknowledgementRequired"
          render={({ field }) => (
            <StylishNewCheckbox
              label="Request that Recipients Confirm Receipt"
              id="acknowledgementRequired"
              checked={!!field.value}
              onChange={(e) => field.onChange(e.target.checked)}
            />
          )}
        />
      </div>
      {acknowledgementRequired && (
        <div className="mb-3">
          <label className="form-label">How recipients respond</label>
          <StylishNewToggleButtonGroup
            data={RESPONSE_PRESET_CHOICES}
            selected={responsePreset}
            onChange={(value) => setResponsePreset(value as ResponsePresetKey)}
            onBlur={() => {}}
          />
          {selectedChannels?.includes(NotificationType.SMS) ? (
            <p className="tw:mt-2 tw:mb-0 tw:text-xs tw:text-muted-foreground">
              Text recipients will read: “
              {describeSmsResponseOptions(RESPONSE_PRESETS[responsePreset])}”
            </p>
          ) : null}
          <p className="tw:mt-1 tw:mb-0 tw:text-xs tw:text-muted-foreground">
            PRATUS and Teams recipients confirm with a button. Email recipients
            are pointed back to PRATUS, since replies to email are not received.
          </p>
        </div>
      )}
      {acknowledgementRequired && (
        <EscalationPlanField
          preset={escalationPreset}
          onPresetChange={setEscalationPreset}
          plan={escalationPlan}
        />
      )}
      <div data-tour="notifications-recipients">
        <FormSelectField
          title="Recipients"
          fieldName="userIds"
          options={(rosters ?? [])
            .filter(
              (person) =>
                !areaFilterActive ||
                everyoneInArea ||
                !audiencePreview.data ||
                matchUserIds.includes(person.user_guid)
            )
            .map((person) => ({
              label: person.email_address,
              value: person.user_guid,
            }))}
          variant="full"
          isMulti={true}
          disabled={everyoneInArea}
        />
        <FormSelectField
          title={rolesTitle}
          fieldName="roleScopeKeys"
          options={roleScopeOptions(roleScopeMembers)}
          variant="full"
          isMulti={true}
          isClearable
          disabled={everyoneInArea}
        />
        {hwcg ? (
          <FormSelectField
            title="Teams"
            fieldName="teamIds"
            options={strikeTeams.map((team) => ({
              label: team.name,
              value: team.strike_team_id || team.id,
            }))}
            variant="full"
            isMulti={true}
            isClearable
          />
        ) : null}
        {peopleProblem && !areaFilterActive ? (
          <p className="tw:mb-3 tw:text-sm tw:text-destructive">
            {peopleProblem}
          </p>
        ) : null}
      </div>
      <div data-tour="notifications-map-area">
        <AudienceAreaField
          audience={areaAudience}
          onChange={setAreaAudience}
          aorOptions={aorOptions}
          preview={audiencePreview.data}
          isPreviewLoading={audiencePreview.isFetching}
          error={areaFilterActive ? peopleProblem : null}
          sendToEveryone={everyoneInArea}
          onSendToEveryoneChange={setSendToEveryone}
        />
      </div>
      <div className="mb-3" data-tour="notifications-attachments">
        <label className="form-label">Attachments</label>
        <p className="tw:mb-2 tw:text-sm tw:text-muted-foreground">
          Attach files or folders from this workspace or your organization File
          Manager.
        </p>
        <WorkspaceFilePicker
          incidentId={associatedIncidentId || undefined}
          allowFolders
          maxFiles={10}
          selected={attachments}
          onChange={setAttachments}
        />
      </div>
      <FormSelectField
        title="Associated Incident"
        fieldName="incidentId"
        options={
          incidents?.map((incident) => ({
            label: incident.name,
            value: incident.id,
          })) || []
        }
        variant="full"
        isClearable
        disabled={!!incidentId}
      />
      <div data-tour="notifications-stand-down">
        <FormSelectField
          title="Severity *"
          fieldName="priority"
          options={Object.values(NotificationPriority).map((value) => ({
            label: severityLabel(value),
            value,
          }))}
          variant="full"
          onChangeCallback={(value) => {
            if (
              value === NotificationPriority.CRITICAL ||
              value === NotificationPriority.STAND_DOWN_SAFETY
            ) {
              setDeliveryMode(DeliveryMode.NOW);
              form.setValue('scheduledDate', null);
            }
          }}
        />
        {selectedPriority === NotificationPriority.STAND_DOWN_SAFETY ? (
          <p className="tw:mb-3 tw:rounded-md tw:border tw:border-destructive/40 tw:bg-destructive/10 tw:px-3 tw:py-2 tw:text-sm tw:text-foreground">
            A Stand Down Safety notification will be sent to all recipients
            immediately via all available channels. Scheduled delivery is locked
            to Send now.
          </p>
        ) : null}
      </div>
      <div data-tour="notifications-schedule">
        {allowSchedule &&
        selectedPriority !== NotificationPriority.CRITICAL &&
        selectedPriority !== NotificationPriority.STAND_DOWN_SAFETY ? (
          <div className="mb-3">
            <StylishNewToggleButtonGroup
              data={[
                { label: 'Send now', value: DeliveryMode.NOW },
                {
                  label: 'Schedule delivery',
                  value: DeliveryMode.SCHEDULE,
                },
              ]}
              selected={deliveryMode}
              onChange={(value) => {
                setDeliveryMode(
                  value as DeliveryMode.SCHEDULE | DeliveryMode.NOW
                );
                if (value === DeliveryMode.NOW) {
                  form.setValue('scheduledDate', null);
                }
              }}
              onBlur={() => {}}
            />
          </div>
        ) : null}
        {allowSchedule &&
        deliveryMode === DeliveryMode.SCHEDULE &&
        selectedPriority !== NotificationPriority.CRITICAL &&
        selectedPriority !== NotificationPriority.STAND_DOWN_SAFETY ? (
          <FormDateTimeField title="Scheduled Date" fieldName="scheduledDate" />
        ) : null}
        {selectedPriority === NotificationPriority.STAND_DOWN_SAFETY ||
        selectedPriority === NotificationPriority.CRITICAL ? (
          <p className="tw:mb-3 tw:text-xs tw:text-muted-foreground">
            Delivery is locked to Send now for this severity.
          </p>
        ) : null}
      </div>
    </FormProvider>
  );
});

export default NotificationComposeFields;
